import { Button, ButtonGroup, Link } from 'Components/Core';
import { Grid, PageSection } from 'Components/Layout';
import { GridFieldSection } from 'Components/Layout/FieldSection';
import { RootState } from 'Data/Redux';
import { EquipmentDriverListItemModel } from 'Data/ViewModels/ListItemModel';
import { hasNonEmptyValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import deepEquals from 'Lib/Object/deepEquals';
import { ascend, isNil, sort, uniq } from 'ramda';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { actions } from '../../Data/ReduxViewModels/Admin';
import { EquipmentTypesByQestId } from '../Equipment/equipmentTypes';

const EquipmentDriverLink: React.FC<{ id: Guid; name?: string }> = ({ id, name }) => (
  <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
    <Link to={`/admin/equipment-drivers/${id}`}>{hasNonEmptyValue(name) ? name : '<Unnamed>'}</Link>
  </div>
);

const EquipmentDriversAdmin: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const creating: boolean = useSelector((state: RootState) => state.admin.equipmentDriver.create.creating);
  const created: boolean = useSelector((state: RootState) => state.admin.equipmentDriver.create.created);
  const idForNewItem: string | null = useSelector((state: RootState) => state.admin.equipmentDriver.create.id);

  // Have to convert reducer to typescript to use RootState for below. So use type of any for now
  const items: EquipmentDriverListItemModel[] = useSelector((state: any) => state.admin.syncModel.Items, deepEquals);

  useEffect(() => {
    if (created) {
      navigate(`/admin/equipment-drivers/${idForNewItem}`);
    }
  }, [navigate, dispatch, created, idForNewItem]);

  if (isNil(items)) {
    return null;
  }

  // Filtering the equipment drivers which supports interfacing and sorting them by Equipment Type Name
  const sortedEquipmentTypeIdsByName = sort(
    ascend((i) => EquipmentTypesByQestId[i].name),
    uniq(items.map((eachItem) => parseInt(eachItem.AdditionalProperties.EquipmentType))).filter(
      (currentId) => EquipmentTypesByQestId[currentId].isInterfacingSupported
    )
  );

  const deleteEquipmentDriver = (elementId: string) => {
    dispatch(actions.deleteEquipmentDriver({ elementId }));
  };

  const createNewEquipmentDriver = () => {
    dispatch(
      actions.createEquipmentDriver({ defaultEquipmentDriver: { Name: 'New Driver', EquipmentType: { Id: '30005' } } })
    );
  };

  const filteredItemsByEquipmentId = (id: number) =>
    items.filter((e) => parseInt(e.AdditionalProperties.EquipmentType) === id);

  return (
    <PageSection>
      {sortedEquipmentTypeIdsByName.map((id) => (
        <GridFieldSection heading={`${EquipmentTypesByQestId[id].name} Drivers`} key={id}>
          <Grid
            removeConfirm={{
              confirm: true,
              confirmTitle: 'Confirm',
              confirmText: 'Are you sure you want to remove this driver?',
              confirmButtonText: 'Delete Driver',
            }}
            data={filteredItemsByEquipmentId(id).map((item) => [
              <EquipmentDriverLink name={item.Label} id={item.Value} />,
            ])}
            onRemoveRow={(i) => deleteEquipmentDriver(filteredItemsByEquipmentId(id)[i].Value)}
            colCellAlignments="left"
            colWidths="350px"
          />
        </GridFieldSection>
      ))}
      <ButtonGroup>
        <Button primary onClick={createNewEquipmentDriver} loading={creating}>
          Add Driver
        </Button>
      </ButtonGroup>
    </PageSection>
  );
};

export default EquipmentDriversAdmin;
